/**
 * AmibrokerWatchlistModal
 *
 * Shows the market-watch scrips in a table with per-symbol TodayFill /
 * BackFill / Remove controls and bulk actions.
 *
 * Opens when window dispatches the "open-amibroker-modal" CustomEvent.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { ScripState } from "@store/watchlist-slice";
import {
  pushSymbolScripIdMap,
  pushBackfillDataToAmibroker,
  pushTodayFillDataToAmibroker,
  notifyAmibrokerLogin,
} from "@service/ipc-handler";
import { createWatchlistInAmibrokerHandler } from "@service/watchlist-handler";
import { chartBackfillHandler } from "@service/chart-backfill-handler";
import { OneMinuteBar } from "../../types/chart.types";
// axios no longer used directly (backfill routed through chartBackfillHandler.fetchBackfillForDays)
import "./AmibrokerWatchlistModal.scss";

// ── Constants ─────────────────────────────────────────────────────
const DEFAULT_CANDLE_INTERVAL = "1M";

type FillStatus = "idle" | "loading" | "done" | "error";

interface SymbolStatus {
  todayfill: FillStatus;
  backfill: FillStatus;
}

// ── Helper ────────────────────────────────────────────────────────
function barsToPayload(
  bars: OneMinuteBar[],
): [string, number, number, number, number, number, number][] {
  return bars.map((bar) => {
    const d = new Date(bar.timestamp);
    const pad = (n: number) => String(n).padStart(2, "0");
    const ts = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    return [ts, bar.open, bar.high, bar.low, bar.close, bar.volume, bar.oi];
  });
}

// ── Component ─────────────────────────────────────────────────────
const AmibrokerWatchlistModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Local working copy of symbols — seeded from watchlist, user can add/remove
  const [symbols, setSymbols] = useState<ScripState[]>([]);
  const [statusMap, setStatusMap] = useState<Record<number, SymbolStatus>>({});
  const [globalStatus, setGlobalStatus] = useState("");
  const [selectedCandleType, setSelectedCandleType] = useState(DEFAULT_CANDLE_INTERVAL);
  const [selectedDays, setSelectedDays] = useState("");
  const [sortCol, setSortCol] = useState<"symbol" | "segment" | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  // Store selectors
  const watchlistScrips: ScripState[] = useSelector(
    (state: any) => state.watchlist?.scrips || [],
  );
  const symbolScripIdMap: Record<string, string> = useSelector(
    (state: any) => state.metadata?.feedsScripsState?.symbolScripId || {},
  );
  const feedsScrips: any[] = useSelector(
    (state: any) => state.metadata?.feedsScripsState?.feedsScrips || [],
  );
  const userWatchlist: any[] = useSelector(
    (state: any) => state.metadata?.marketWatchState?.userWatchlist || [],
  );
  const defaultWatchlist: any[] = useSelector(
    (state: any) => state.metadata?.marketWatchState?.defaultWatchlist || [],
  );
  const supportedBackfills: { candleType: string; backfillDays: string[] }[] = useSelector(
    (state: any) => state.metadata?.marketWatchState?.supportedBackfills || [],
  );

  // Sort handler for column headers
  const handleSort = (col: "symbol" | "segment") => {
    if (sortCol === col) setSortAsc((a) => !a);
    else { setSortCol(col); setSortAsc(true); }
  };

  // Sorted view of symbols
  const sortedSymbols = useMemo(() => {
    if (!sortCol) return symbols;
    return [...symbols].sort((a, b) => {
      const av = (sortCol === "symbol" ? a.symbol : a.segment || "").toLowerCase();
      const bv = (sortCol === "symbol" ? b.symbol : b.segment || "").toLowerCase();
      return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [symbols, sortCol, sortAsc]);

  // Derive days list for the currently selected candle type
  const backfillDays = useMemo(() => {
    const match = supportedBackfills.find((b) => b.candleType === selectedCandleType);
    return match?.backfillDays || [];
  }, [supportedBackfills, selectedCandleType]);

  // Keep selectedDays in sync when the days list changes
  useEffect(() => {
    if (backfillDays.length > 0 && !backfillDays.includes(selectedDays)) {
      setSelectedDays(backfillDays[0]);
    }
  }, [backfillDays, selectedDays]);


  // ── Open/close ───────────────────────────────────────────────────
  const openModal = useCallback(() => {
    setSymbols(watchlistScrips.map((s) => ({ ...s })));
    setStatusMap({});
    setGlobalStatus("");
    setIsOpen(true);
  }, [watchlistScrips]);

  useEffect(() => {
    window.addEventListener("open-amibroker-modal", openModal);
    return () => window.removeEventListener("open-amibroker-modal", openModal);
  }, [openModal]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen]);

  // ── Status helpers ───────────────────────────────────────────────
  const setSymStatus = (id: number, type: "todayfill" | "backfill", status: FillStatus) => {
    setStatusMap((prev) => ({
      ...prev,
      [id]: { ...({ todayfill: "idle", backfill: "idle", ...prev[id] }), [type]: status },
    }));
  };

  // ── Fill operations ──────────────────────────────────────────────
  const doTodayFill = async (scrip: ScripState) => {
    setSymStatus(scrip.id, "todayfill", "loading");
    try {
      const { bars } = await chartBackfillHandler.fetchTodayfill(scrip.id);
      if (bars.length > 0) {
        await pushTodayFillDataToAmibroker({
          symbol: scrip.symbol,
          candleInterval: DEFAULT_CANDLE_INTERVAL,
          payload: barsToPayload(bars),
        });
      }
      setSymStatus(scrip.id, "todayfill", "done");
    } catch (e) {
      console.error("[AmiBroker] TodayFill failed for", scrip.symbol, e);
      setSymStatus(scrip.id, "todayfill", "error");
    }
  };

  const doBackFill = async (scrip: ScripState) => {
    setSymStatus(scrip.id, "backfill", "loading");
    try {
      const bars = await chartBackfillHandler.fetchBackfillForDays(
        scrip.id,
        selectedDays || "5",
        selectedCandleType || DEFAULT_CANDLE_INTERVAL,
      );
      if (bars.length > 0) {
        await pushBackfillDataToAmibroker({
          symbol: scrip.symbol,
          candleInterval: selectedCandleType || DEFAULT_CANDLE_INTERVAL,
          payload: barsToPayload(bars),
        });
      }
      setSymStatus(scrip.id, "backfill", "done");
    } catch (e) {
      console.error("[AmiBroker] BackFill failed for", scrip.symbol, e);
      setSymStatus(scrip.id, "backfill", "error");
    }
  };

  // ── Bulk operations ──────────────────────────────────────────────
  const todayFillAll = async () => {
    setGlobalStatus("Running Today Fill...");
    for (const s of symbols) {
      await doTodayFill(s);
    }
    setGlobalStatus("Today Fill complete.");
  };

  const backFillAll = async () => {
    setGlobalStatus("Running Back Fill...");
    for (const s of symbols) {
      await doBackFill(s);
    }
    setGlobalStatus("Back Fill complete.");
  };

  // ── Sync / Reconnect ─────────────────────────────────────────────
  const syncSymbols = async () => {
    setGlobalStatus("Syncing symbols...");
    await pushSymbolScripIdMap(symbolScripIdMap);
    setGlobalStatus("Symbol map synced.");
  };

  const syncWatchlist = async () => {
    setGlobalStatus("Syncing watchlist...");
    await createWatchlistInAmibrokerHandler({ feedsScrips, userWatchlist, defaultWatchlist });
    setGlobalStatus("Watchlist synced.");
  };

  const reconnect = async () => {
    setGlobalStatus("Reconnecting...");
    await notifyAmibrokerLogin();
    setGlobalStatus("Reconnect sent.");
  };

  // ── Status icon ───────────────────────────────────────────────────
  const statusIcon = (status: FillStatus) => {
    if (status === "loading") return <span className="ami-status ami-status-loading">⟳</span>;
    if (status === "done") return <span className="ami-status ami-status-done">✓</span>;
    if (status === "error") return <span className="ami-status ami-status-error">✗</span>;
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="ami-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setIsOpen(false); }}>
      <div className="ami-modal">
        {/* Header */}
        <div className="ami-modal-header">
          <span className="ami-modal-title">
            AmiBroker Watchlist
            {symbols.length > 0 && (
              <span className="ami-title-count"> ({symbols.length} symbol{symbols.length !== 1 ? "s" : ""})</span>
            )}
          </span>
          <button className="ami-modal-close" onClick={() => setIsOpen(false)}>×</button>
        </div>

        {/* Controls row */}
        <div className="ami-controls">
          <div className="ami-controls-left">
            <button className="ami-btn ami-btn-teal" onClick={syncSymbols}>Sync Symbols</button>
            <button className="ami-btn ami-btn-purple" onClick={syncWatchlist}>Sync Watchlist</button>
            <button className="ami-btn ami-btn-orange" onClick={reconnect}>Reconnect</button>
          </div>

        </div>

        {/* Table */}
        <div className="ami-table-wrapper">
          <table className="ami-table">
            <thead>
              <tr>
                <th>S.NO</th>
                <th
                  className={`ami-th-sortable${sortCol === "symbol" ? " active" : ""}`}
                  onClick={() => handleSort("symbol")}
                >
                  SYMBOL
                  <span className="ami-sort-icon">
                    {sortCol === "symbol" ? (sortAsc ? " ▲" : " ▼") : " ⇅"}
                  </span>
                </th>
                <th
                  className={`ami-th-sortable${sortCol === "segment" ? " active" : ""}`}
                  onClick={() => handleSort("segment")}
                >
                  SEGMENT
                  <span className="ami-sort-icon">
                    {sortCol === "segment" ? (sortAsc ? " ▲" : " ▼") : " ⇅"}
                  </span>
                </th>
                <th>TODAY FILL</th>
                <th>BACK FILL</th>
              </tr>
            </thead>
            <tbody>
              {symbols.length === 0 ? (
                <tr>
                  <td colSpan={5} className="ami-empty">No symbols in market watch.</td>
                </tr>
              ) : (
                sortedSymbols.map((scrip, idx) => {
                  const st = statusMap[scrip.id] || { todayfill: "idle", backfill: "idle" };
                  return (
                    <tr key={scrip.id}>
                      <td>{idx + 1}</td>
                      <td className="ami-symbol">{scrip.symbol}</td>
                      <td className="ami-segment">{scrip.segment || "—"}</td>
                      <td>
                        <button
                          className={`ami-fill-btn ami-fill-btn--${st.todayfill}${st.todayfill === "loading" ? " loading" : ""}`}
                          onClick={() => doTodayFill(scrip)}
                          disabled={st.todayfill === "loading"}
                          title="Today Fill"
                        >
                          {statusIcon(st.todayfill)}
                          {st.todayfill === "idle" ? "Fill" : st.todayfill === "loading" ? "..." : st.todayfill === "done" ? "Done" : "Retry"}
                        </button>
                      </td>
                      <td>
                        <button
                          className={`ami-fill-btn ami-fill-btn--${st.backfill}${st.backfill === "loading" ? " loading" : ""}`}
                          onClick={() => doBackFill(scrip)}
                          disabled={st.backfill === "loading"}
                          title="Back Fill"
                        >
                          {statusIcon(st.backfill)}
                          {st.backfill === "idle" ? "Fill" : st.backfill === "loading" ? "..." : st.backfill === "done" ? "Done" : "Retry"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="ami-modal-footer">
          <div className="ami-select-group">
            <label htmlFor="ami-timeframe-select">Timeframe</label>
            <select
              id="ami-timeframe-select"
              className="ami-select"
              value={selectedCandleType}
              onChange={(e) => setSelectedCandleType(e.target.value)}
            >
              {supportedBackfills.length > 0
                ? supportedBackfills.map((b) => (
                    <option key={b.candleType} value={b.candleType}>{b.candleType}</option>
                  ))
                : <option value={DEFAULT_CANDLE_INTERVAL}>{DEFAULT_CANDLE_INTERVAL}</option>
              }
            </select>
          </div>
          <div className="ami-select-group">
            <label htmlFor="ami-days-select">Days</label>
            <select
              id="ami-days-select"
              className="ami-select"
              value={selectedDays}
              onChange={(e) => setSelectedDays(e.target.value)}
            >
              {backfillDays.length > 0
                ? backfillDays.map((d) => <option key={d} value={d}>{d}</option>)
                : ["1","3","5","10","30"].map((d) => <option key={d} value={d}>{d}</option>)
              }
            </select>
          </div>
          <button className="ami-btn ami-btn-cyan" onClick={todayFillAll}>TODAY FILL ALL</button>
          <button className="ami-btn ami-btn-green" onClick={backFillAll}>BACK FILL ALL</button>
          {globalStatus && <span className="ami-global-status">{globalStatus}</span>}
        </div>
      </div>
    </div>
  );
};

export default AmibrokerWatchlistModal;
